import { useToastController } from "@tamagui/toast";
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { YStack } from "tamagui";

import {
  useAdvanceUserProgressionToNextStep,
  useGetOrCreateUserProgression,
} from "@/api/generated";
import {
  ProgressionStep,
  type UserProgressionStateModel,
  UserProgressionStatus,
  VerificationStatus,
} from "@/api/generated/models";
import {
  useIsChatMutationPending,
  useSendNoriMessage,
} from "@/api/mutations/conversation";
import { useUpdateUserProfile } from "@/api/mutations/userProfile";
import { useGetUserProfile } from "@/api/queries/userProfile";
import Loading from "@/auth/Loading";
import type { SIGN_IN_FLOW } from "@/auth/SignIn";
import { usePhoneCodeAuth } from "@/auth/usePhoneCodeAuth";
import {
  Button,
  ConstrainedView,
  Paragraph,
  Skeleton,
  XStack,
} from "@/components";
import { useAskUserQuestionContainerStyles } from "@/conversation/AskUserQuestionsPanel";
import { QuestionScrollArea } from "@/conversation/AskUserQuestionsPanel/OptionParts";
import PanelHeader from "@/conversation/AskUserQuestionsPanel/PanelHeader";
import ConfirmDetailsStep from "@/conversation/PreClinicSteps/ConfirmDetailsStep";
import ConfirmIdVerification from "@/conversation/sidebar/UserProgression/ConfirmIdVerification";
import StartDatabaseIdVerification from "@/conversation/sidebar/UserProgression/StartDatabaseIdVerification";
import type { AddressFormData } from "@/forms/AddressFields";
import { useCurrentConversationId } from "@/hooks/conversation";
import { captureException } from "@/sentry";
import { useActiveProgressionStore } from "@/store/activeProgression";
import { usePreAsyncSetupStore } from "@/store/preAsyncSetup";
import HomeAddressCard from "./HomeAddressCard";
import PhoneNumberCard from "./PhoneNumberCard";
import VerificationCodeCard from "./VerificationCodeCard";

// Sent once setup is done, so the agent picks the conversation back up.
const DETAILS_ENTERED_MESSAGE = "I've entered my details.";
const CONFIRM_DETAILS_TITLE = "Confirm your details";
const INCORRECT_CODE_MESSAGE = "That code didn't match. Please try again.";
const FAILED_CODE_MESSAGE = "We couldn't check that code. Please try again.";
const REQUEST_FAILED_TITLE = "Something went wrong. Please try again.";
const PHOTO_ID_TITLE = "Verify your identity with a photo ID";
const PHOTO_ID_DESCRIPTION =
  "We couldn't confirm your identity from the details you entered. You'll take a photo of a " +
  "government-issued ID and a quick selfie with Persona, our ID verification service. It " +
  "takes about 2 minutes.";
const SUPPORT_URL = "https://www.curaihealth.com/support";
const SIGN_IN_FAILED_MESSAGE = "We couldn't sign you in. Please try again.";
const SAVE_DETAILS_ERROR_MESSAGE =
  "We couldn't save your address. Please try again.";

// Module-level so it outlives the remount that swapping the Clerk session can cause: `Loading`
// reads it after sign-in to complete the matching sign-up or sign-in.
const signInFlowRef: RefObject<SIGN_IN_FLOW> = { current: "sign-up" };

const noop = () => {};

/** Holds the panel's shape while the progression loads or a step runs without a screen. */
function PanelSkeleton({ height }: { height?: number }) {
  return (
    <YStack
      height={height}
      gap="$200"
      paddingHorizontal="$150"
      paddingBottom="$200"
    >
      <Skeleton.Text lines={2} widths={["100%", "70%"]} />
      <Skeleton height="$500" borderRadius="$200" />
    </YStack>
  );
}

/** Shown in place of a step whose request failed, so the patient can retry instead of being stuck. */
function RequestFailedCard({ onRetry }: { onRetry: () => void }) {
  return (
    <>
      <PanelHeader
        question={REQUEST_FAILED_TITLE}
        index={0}
        total={1}
        onPrev={noop}
        onNext={noop}
      />
      <YStack paddingHorizontal="$150" paddingBottom="$150">
        <Button
          mode="primary"
          onPress={onRetry}
          dataTrackingId="pre-async-setup-retry"
        >
          Try again
        </Button>
      </YStack>
    </>
  );
}

function PhotoIdIntroCard({
  onVerify,
  isPlaceholder = false,
}: {
  onVerify: () => void;
  isPlaceholder?: boolean;
}) {
  return (
    <>
      <PanelHeader
        question={PHOTO_ID_TITLE}
        index={0}
        total={1}
        onPrev={noop}
        onNext={noop}
      />
      <YStack gap="$250" paddingHorizontal="$150" paddingBottom="$150">
        <Paragraph>{PHOTO_ID_DESCRIPTION}</Paragraph>
        <XStack justifyContent="flex-end" gap="$150">
          <Button
            mode="secondary"
            disabled={isPlaceholder}
            onPress={() => window.open(SUPPORT_URL, "_blank")}
            dataTrackingId="pre-async-setup-contact-support"
          >
            Contact support
          </Button>
          <Button
            mode="primary"
            disabled={isPlaceholder}
            onPress={onVerify}
            dataTrackingId="pre-async-setup-verify-id"
          >
            Verify my ID
          </Button>
        </XStack>
      </YStack>
    </>
  );
}

// The skeleton sits over an invisible copy of the photo ID card, so it is always that card's height.
function PhotoIdCardSkeleton() {
  return (
    <YStack>
      <YStack opacity={0} pointerEvents="none" aria-hidden>
        <PhotoIdIntroCard onVerify={noop} isPlaceholder />
      </YStack>
      <YStack position="absolute" top={0} right={0} bottom={0} left={0}>
        <PanelSkeleton />
      </YStack>
    </YStack>
  );
}

function ConfirmVerificationStep({ onContinue }: { onContinue: () => void }) {
  const { data: profile } = useGetUserProfile();
  const [hasChosenPhotoId, setHasChosenPhotoId] = useState(false);

  const needsPhotoId =
    profile?.idVerification?.status === VerificationStatus.pending_fallback;
  if (needsPhotoId && !hasChosenPhotoId) {
    return <PhotoIdIntroCard onVerify={() => setHasChosenPhotoId(true)} />;
  }

  return (
    <ConfirmIdVerification
      onContinue={onContinue}
      onCancelFallback={() => setHasChosenPhotoId(false)}
      loadingIndicator={<PhotoIdCardSkeleton />}
      canRenderPersonaInline={false}
    />
  );
}

function PhoneStep({ onCodeSent }: { onCodeSent: () => void }) {
  const { show: showToast } = useToastController();
  const { sendCode } = usePhoneCodeAuth(signInFlowRef);
  const phone = usePreAsyncSetupStore((s) => s.phone);
  const setPhone = usePreAsyncSetupStore((s) => s.setPhone);
  const [isSending, setIsSending] = useState(false);

  const submit = async () => {
    setIsSending(true);
    try {
      const errorMessage = await sendCode(phone);
      if (errorMessage) {
        showToast(errorMessage, { duration: 10000 });
        return;
      }

      onCodeSent();
    } finally {
      setIsSending(false);
    }
  };

  return (
    <PhoneNumberCard
      phone={phone}
      onChangePhone={setPhone}
      onSubmit={submit}
      isSending={isSending}
    />
  );
}

function CodeStep({
  onChangeNumber,
  onVerified,
}: {
  onChangeNumber: () => void;
  onVerified: () => void;
}) {
  const { show: showToast } = useToastController();
  const { verifyCode, resendCode, activateSession } =
    usePhoneCodeAuth(signInFlowRef);
  const phone = usePreAsyncSetupStore((s) => s.phone);
  const goToAuthScreen = usePreAsyncSetupStore((s) => s.goToAuthScreen);
  const [code, setCode] = useState("");
  const [errorText, setErrorText] = useState<string | undefined>();
  const [isVerifying, setIsVerifying] = useState(false);

  const changeCode = (value: string) => {
    setCode(value);
    setErrorText(undefined);
  };

  const submitCode = async (value: string) => {
    setIsVerifying(true);
    setErrorText(undefined);
    const result = await verifyCode(value);
    if (result.outcome === "verified") {
      await signInWithSession(result.sessionId);
      return;
    }

    setIsVerifying(false);
    if (result.outcome === "incorrect") {
      setCode("");
      setErrorText(INCORRECT_CODE_MESSAGE);
      return;
    }
    // Clerk's own message, as the full-screen sign-in shows it (e.g. a rate limit or full user quota).
    if (result.errorMessage) {
      showToast(result.errorMessage, { duration: 10000 });
      return;
    }

    setErrorText(FAILED_CODE_MESSAGE);
  };

  const signInWithSession = async (sessionId: string | null) => {
    try {
      // A verified sign-up still missing requirements has no session to swap in.
      if (!sessionId)
        throw new Error("Clerk returned no session for a verified code.");

      // Before the session swap, which can remount the conversation.
      onVerified();
      await activateSession(sessionId);
    } catch (error) {
      captureException("Failed to sign in from the pre-async setup panel", {
        extra: { error, flow: signInFlowRef.current },
      });
      // A toast, because a failed swap has already unmounted this card for a fresh one; the resets
      // only matter when there was no session and the card never left.
      goToAuthScreen("code");
      setIsVerifying(false);
      setCode("");
      showToast(SIGN_IN_FAILED_MESSAGE, { duration: 10000 });
    }
  };

  const resend = async () => {
    setCode("");
    setErrorText(undefined);
    const errorMessage = await resendCode();
    if (errorMessage) {
      showToast(errorMessage, { duration: 10000 });
    }
  };

  return (
    <VerificationCodeCard
      phone={phone}
      code={code}
      onChangeCode={changeCode}
      onSubmitCode={submitCode}
      onResend={resend}
      onChangeNumber={onChangeNumber}
      isVerifying={isVerifying}
      errorText={errorText}
    />
  );
}

/**
 * The progression's authentication step: phone number, then code, then `Loading`, which finishes
 * the sign-up or sign-in (and migrates the anonymous user's progression) the way every other
 * sign-in does.
 */
function AuthenticationStep({
  progression,
  onSignedIn,
}: {
  progression: UserProgressionStateModel;
  onSignedIn: () => void;
}) {
  const authScreen = usePreAsyncSetupStore((s) => s.active?.authScreen);
  const goToAuthScreen = usePreAsyncSetupStore((s) => s.goToAuthScreen);
  // The signing-in skeleton takes the code card's height, so the panel doesn't jump between them.
  const [codeCardHeight, setCodeCardHeight] = useState<number>();
  const setActiveProgression = useActiveProgressionStore(
    (s) => s.setActiveProgression,
  );
  const isMarkedActive = useActiveProgressionStore(
    (s) => s.activeProgression?.id === progression.id,
  );

  // Marks a progression as underway, so `Loading` doesn't start the partner flow over this one.
  useEffect(() => {
    setActiveProgression(progression);
    return () => setActiveProgression(null);
  }, [progression, setActiveProgression]);

  // `Loading` reads the active progression once, when it completes, and its effects run before this
  // step's, so it waits until the mark is in (e.g. after the session swap remounts the conversation).
  if (authScreen === "signing_in" && !isMarkedActive) {
    return <PanelSkeleton height={codeCardHeight} />;
  }
  if (authScreen === "signing_in") {
    return (
      <Loading
        signInFlowRef={signInFlowRef}
        conversationId={progression.conversation_id ?? undefined}
        onComplete={onSignedIn}
        isEmbedded
        loadingIndicator={<PanelSkeleton height={codeCardHeight} />}
      />
    );
  }

  if (authScreen === "code") {
    return (
      <YStack
        onLayout={(event) => setCodeCardHeight(event.nativeEvent.layout.height)}
      >
        <CodeStep
          onChangeNumber={() => goToAuthScreen("phone")}
          onVerified={() => goToAuthScreen("signing_in")}
        />
      </YStack>
    );
  }

  return <PhoneStep onCodeSent={() => goToAuthScreen("code")} />;
}

/**
 * The progression's profile step, as two cards: the patient's details, then the home address,
 * which together cover what the database identity check needs.
 */
function ProfileStep({ onSaved }: { onSaved: () => Promise<void> }) {
  const { show: showToast } = useToastController();
  const { data: profile } = useGetUserProfile();
  const { mutateAsync: updateUserProfile, isPending } = useUpdateUserProfile();
  const [page, setPage] = useState<"details" | "address">("details");

  const saveAddress = async (address: AddressFormData) => {
    try {
      await updateUserProfile({
        data: { ...address, residingState: address.state },
      });
    } catch (error) {
      captureException("Failed to save the pre-async setup address", {
        extra: { error },
      });
      showToast(SAVE_DETAILS_ERROR_MESSAGE);
      return;
    }

    await onSaved();
  };

  if (page === "details") {
    return (
      <>
        <PanelHeader
          question={CONFIRM_DETAILS_TITLE}
          index={0}
          total={1}
          onPrev={noop}
          onNext={noop}
        />
        <QuestionScrollArea>
          <ConfirmDetailsStep onSubmitted={() => setPage("address")} />
        </QuestionScrollArea>
      </>
    );
  }

  return (
    <HomeAddressCard
      defaultValues={{
        streetAddress: profile?.streetAddress,
        streetAddress2: profile?.streetAddress2,
        city: profile?.city,
        state: profile?.state,
        zipCode: profile?.zipCode?.slice(0, 5),
      }}
      onSubmit={saveAddress}
      isSubmitting={isPending}
    />
  );
}

/**
 * The setup panel above the chat bar, opened by `usePreAsyncSetupSession`. It walks the
 * `pre_async` user progression, so the backend decides which steps a patient still needs and
 * records each one, while the panel renders them as cards in the chat instead of the full-screen
 * flow. Once the progression completes it sends a message so the agent picks the conversation back
 * up.
 */
export default function PreAsyncSetupPanel() {
  const conversationId = useCurrentConversationId();
  const active = usePreAsyncSetupStore((s) => s.active);
  const setProgression = usePreAsyncSetupStore((s) => s.setProgression);
  const clear = usePreAsyncSetupStore((s) => s.clear);
  const { sendNoriMessage } = useSendNoriMessage(conversationId);
  const isReplyStreaming = useIsChatMutationPending();
  const containerStyles = useAskUserQuestionContainerStyles();
  const { mutateAsync: getOrCreateUserProgression } =
    useGetOrCreateUserProgression({
      mutation: { meta: { suppressErrorToast: true } },
    });
  const { mutateAsync: advanceToNextStep } =
    useAdvanceUserProgressionToNextStep({
      mutation: { meta: { suppressErrorToast: true } },
    });
  const isFetchingProgressionRef = useRef(false);
  const hasFinishedRef = useRef(false);
  const [failedRequest, setFailedRequest] = useState<
    (() => Promise<boolean>) | null
  >(null);

  const isForThisConversation = active?.conversationId === conversationId;
  const progression = isForThisConversation ? active?.progression : null;
  const needsProgression = isForThisConversation && !progression;

  const fetchProgression =
    useCallback(async (): Promise<UserProgressionStateModel | null> => {
      try {
        return await getOrCreateUserProgression({
          data: { flow_name: "pre_async", conversation_id: conversationId },
        });
      } catch (error) {
        captureException("Failed to load the pre_async progression", {
          extra: { error, conversationId },
        });
        return null;
      }
    }, [conversationId, getOrCreateUserProgression]);

  // Runs a request the panel can't move on without; a failure leaves a retry card instead of
  // closing the panel, which would otherwise stay closed for this event.
  const runRequest = useCallback(async (request: () => Promise<boolean>) => {
    setFailedRequest(null);
    const hasSucceeded = await request();
    if (!hasSucceeded) setFailedRequest(() => request);
  }, []);

  const loadProgression = useCallback(async (): Promise<boolean> => {
    const loaded = await fetchProgression();
    if (!loaded) return false;

    setProgression(loaded);
    return true;
  }, [fetchProgression, setProgression]);

  useEffect(() => {
    if (!needsProgression || isFetchingProgressionRef.current) return;

    isFetchingProgressionRef.current = true;
    runRequest(loadProgression).finally(() => {
      isFetchingProgressionRef.current = false;
    });
  }, [needsProgression, runRequest, loadProgression]);

  const advanceFrom = useCallback(
    async (current: UserProgressionStateModel): Promise<boolean> => {
      if (!current.current_step) return true;

      try {
        const updated = await advanceToNextStep({
          progressionId: current.id,
          data: { step_id: current.current_step },
        });
        setProgression(updated);
        return true;
      } catch (error) {
        captureException("Failed to advance the pre_async progression", {
          extra: { error, step: current.current_step },
        });
        return false;
      }
    },
    [advanceToNextStep, setProgression],
  );

  const advanceCurrent = useCallback(async (): Promise<boolean> => {
    const current = usePreAsyncSetupStore.getState().active?.progression;
    if (!current) return true;

    return advanceFrom(current);
  }, [advanceFrom]);

  const advance = useCallback(
    () => runRequest(advanceCurrent),
    [runRequest, advanceCurrent],
  );

  // Signing in can migrate the anonymous user's progression, so it is reloaded before advancing.
  const reloadAfterSignIn = useCallback(async (): Promise<boolean> => {
    const reloaded = await fetchProgression();
    if (!reloaded) return false;

    const isStillOnAuthentication =
      reloaded.current_step === ProgressionStep.authentication;
    if (isStillOnAuthentication) return advanceFrom(reloaded);

    setProgression(reloaded);
    return true;
  }, [fetchProgression, advanceFrom, setProgression]);

  const continueAfterSignIn = useCallback(
    () => runRequest(reloadAfterSignIn),
    [runRequest, reloadAfterSignIn],
  );

  const finish = useCallback(() => {
    if (hasFinishedRef.current) return;

    hasFinishedRef.current = true;
    clear();
    sendNoriMessage(DETAILS_ENTERED_MESSAGE);
  }, [clear, sendNoriMessage]);

  const isProgressionDone = Boolean(
    progression &&
      (progression.status === UserProgressionStatus.completed ||
        !progression.current_step),
  );

  useEffect(() => {
    if (isProgressionDone) finish();
  }, [isProgressionDone, finish]);

  // A new setup can start in the same mount once an earlier one has finished and cleared.
  useEffect(() => {
    if (active) return;

    hasFinishedRef.current = false;
    setFailedRequest(null);
  }, [active]);

  if (!active || !isForThisConversation) return null;
  // The panel opens once the reply introducing it has finished.
  if (isReplyStreaming) return null;
  if (isProgressionDone) return null;

  let body: ReactNode = <PanelSkeleton />;
  if (failedRequest) {
    body = <RequestFailedCard onRetry={() => runRequest(failedRequest)} />;
  } else if (progression?.current_step === ProgressionStep.authentication) {
    body = (
      <AuthenticationStep
        progression={progression}
        onSignedIn={continueAfterSignIn}
      />
    );
  } else if (progression?.current_step === ProgressionStep.profile_completion) {
    body = <ProfileStep onSaved={advance} />;
  } else if (
    progression?.current_step === ProgressionStep.start_database_id_verification
  ) {
    body = (
      <StartDatabaseIdVerification
        onContinue={advance}
        loadingIndicator={<PhotoIdCardSkeleton />}
      />
    );
  } else if (
    progression?.current_step === ProgressionStep.confirm_id_verification_status
  ) {
    body = <ConfirmVerificationStep onContinue={advance} />;
  }

  return (
    <ConstrainedView>
      <YStack {...containerStyles}>{body}</YStack>
    </ConstrainedView>
  );
}
